/**
 * @file        src/components/ShareEmailModal.jsx
 * @description CR D2 — email the Ticket/Voucher, Tax Invoice, or Both to the
 *              customer (or an override + CC address) straight from the admin
 *              Booking List / Details drawer, with the real PDF attached.
 *
 *              Generic over `kind` ('flight'|'hotel'|'insurance'). There is no
 *              server-side PDF generation: this component mounts the SAME admin-native
 *              TicketDoc/InvoiceDoc/VoucherDoc/InvoiceDoc page components that
 *              already power the routed /ticket, /invoice, /voucher pages,
 *              off-screen (`position:fixed;left:-9999px` — NOT display:none,
 *              html2canvas cannot rasterize that), waits for each one's own
 *              React Query fetch to settle, rasterizes it with html2canvas and
 *              paginates it into a PDF with jsPDF (lib/pdf.js — a base64-
 *              returning port of the customer site's downloadElementAsPdf),
 *              then POSTs the base64 PDF(s) to POST /:kind/bookings/:id/share-email.
 *              Ticket and Invoice are always built as two SEPARATE PDFs when
 *              "Both" is chosen (never merged into one file).
 *
 *              "Wait for loaded" problem: the doc components fetch their own
 *              data via an internal useQuery with no prop/callback surface for
 *              "I'm done". Rather than reaching into a query instance we don't
 *              own, we poll the off-screen container's own rendered DOM — the
 *              doc components render `.empty` while loading/erroring and
 *              `.doc-sheet` once loaded (see their `isLoading`/`isError`
 *              branches) — which is the more reliable signal here since it
 *              doesn't care which internal library or fetch pattern the doc
 *              page happens to use.
 * @author      Kinetika Labs
 * @created     2026-08-04
 * @changelog
 *   - 2026-09-25 Claude — `kind: 'insurance'`. Its pair is the Tax Invoice and
 *                          the INSURER's own policy document — there is no
 *                          travel document of ours to send, because the
 *                          self-generated "policy certificate" was withdrawn on
 *                          2026-09-25 (§28). The policy document is ALREADY a
 *                          PDF on the server's disk, so it is the one document
 *                          this component does not capture: rasterizing a PDF we
 *                          hold would degrade it to a picture of itself, and the
 *                          server attaches the real file instead.
 *   - 2026-08-04 Claude — Initial (CR D2).
 */
import { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { MemoryRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import Icon from './Icon';
import { toast } from '../lib/toast';
import { elementToPdfBase64 } from '../lib/pdf';
import * as flightBookingsApi from '../api/flightBookings';
import * as hotelBookingsApi from '../api/hotelBookings';
import * as insuranceApi from '../api/insurance';
import FlightTicketDoc from '../pages/flight/TicketDoc';
import FlightInvoiceDoc from '../pages/flight/InvoiceDoc';
import HotelVoucherDoc from '../pages/hotel/VoucherDoc';
import HotelInvoiceDoc from '../pages/hotel/InvoiceDoc';
import InsuranceInvoiceDoc from '../pages/insurance/InvoiceDoc';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Poll an off-screen container until its doc component has rendered its
 *  `.doc-sheet` (loaded) or a "Could not load…" `.empty` message (error). */
function waitForDocReady(container, { timeoutMs = 15000, intervalMs = 150 } = {}) {
  const start = Date.now();
  return new Promise((resolve, reject) => {
    const tick = () => {
      if (container.querySelector('.doc-sheet')) return resolve();
      const empty = container.querySelector('.empty');
      if (empty && /could not load/i.test(empty.textContent || '')) {
        return reject(new Error(empty.textContent.trim()));
      }
      if (Date.now() - start > timeoutMs) {
        return reject(new Error('Timed out preparing the document.'));
      }
      setTimeout(tick, intervalMs);
    };
    tick();
  });
}

/** Mount `Component` off-screen with bookingIdOverride, wait for it to load,
 *  rasterize it to a PDF, and unmount — one throwaway React root + QueryClient
 *  per capture so it never touches the app's own query cache. */
async function captureDocPdf(Component, bookingId) {
  const container = document.createElement('div');
  container.className = 'share-capture';
  Object.assign(container.style, {
    position: 'fixed', left: '-9999px', top: '0', width: '1000px', background: '#fff',
  });
  document.body.appendChild(container);
  const qc = new QueryClient();
  const root = createRoot(container);
  try {
    root.render(
      <QueryClientProvider client={qc}>
        <MemoryRouter>
          <Component bookingIdOverride={bookingId} />
        </MemoryRouter>
      </QueryClientProvider>,
    );
    await waitForDocReady(container);
    // One extra paint so late-loading airline-logo <img> onError fallbacks and
    // web-font swaps have settled before html2canvas snapshots the layout.
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    return await elementToPdfBase64(container);
  } finally {
    root.unmount();
    container.remove();
    qc.clear();
  }
}

/**
 * Per-product wiring. `capture` names the documents this component has to
 * RASTERIZE; anything absent from it the server attaches itself (insurance's
 * policy document is already a PDF on disk).
 */
const KINDS = {
  flight: {
    api: flightBookingsApi,
    primary: { value: 'ticket', label: 'E-Ticket' },
    second: { value: 'invoice', label: 'Tax Invoice' },
    capture: { ticket: FlightTicketDoc, invoice: FlightInvoiceDoc },
    fetchDetails: (id) => flightBookingsApi.getBookingDetails(id),
  },
  hotel: {
    api: hotelBookingsApi,
    primary: { value: 'ticket', label: 'Voucher' },
    second: { value: 'invoice', label: 'Tax Invoice' },
    capture: { ticket: HotelVoucherDoc, invoice: HotelInvoiceDoc },
    fetchDetails: (id) => hotelBookingsApi.getBookingDetails(id),
  },
  insurance: {
    api: insuranceApi,
    // The invoice is FIRST here: it is the document that always exists. The
    // policy document exists only once an admin has uploaded the insurer's PDF
    // against the customer's request, so it is offered conditionally below.
    primary: { value: 'invoice', label: 'Tax Invoice' },
    second: { value: 'policy', label: 'Policy Document' },
    capture: { invoice: InsuranceInvoiceDoc },
    fetchDetails: (id) => insuranceApi.getBooking(id),
  },
};

export default function ShareEmailModal({ kind, booking, onClose }) {
  const cfg = KINDS[kind] || KINDS.flight;
  const { api, primary, second, capture } = cfg;

  // Insurance only: the insurer's document exists once an admin has uploaded it.
  // The list row already carries this (`policy_doc_status`), so the option is
  // never offered for a policy that has none — which would be a send the server
  // refuses, after the admin has typed an address.
  const hasPolicyDoc = kind !== 'insurance' || booking?.policy_doc_status === 'uploaded';
  const options = [primary, ...(hasPolicyDoc ? [second] : [])];
  const canSendBoth = options.length === 2;

  const { data: detailsRes } = useQuery({
    queryKey: [`${kind}-booking-details`, booking.id],
    queryFn: () => cfg.fetchDetails(booking.id),
    enabled: !!booking?.id,
  });
  const details = detailsRes?.data;

  const [document_, setDocument] = useState(primary.value);
  const [to, setTo] = useState(booking?.customer_email || '');
  const [cc, setCc] = useState('');
  const [busy, setBusy] = useState(false);
  const [busyLabel, setBusyLabel] = useState('');
  const [error, setError] = useState('');
  const toTouched = useRef(false);

  // Prefill the "to" field from the authoritative booking details once they
  // arrive, but only if the admin hasn't already started typing an override.
  useEffect(() => {
    if (!toTouched.current && details?.customer_email && !to) setTo(details.customer_email);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details]);

  const toValid = EMAIL_RE.test(to.trim());
  const ccList = cc.split(',').map((s) => s.trim()).filter(Boolean);
  const ccValid = ccList.every((e) => EMAIL_RE.test(e));

  async function handleSend() {
    setError('');
    if (!toValid) { setError('Enter a valid recipient email address.'); return; }
    if (cc && !ccValid) { setError('One or more CC addresses are invalid.'); return; }

    setBusy(true);
    const ref = details?.booking_reference || booking?.booking_reference || booking.id;
    const need = document_ === 'both' ? [primary.value, second.value] : [document_];
    const attachments = [];
    try {
      for (const docType of need) {
        const Component = capture[docType];
        // Not ours to draw: the server attaches this one from its own disk
        // (insurance's policy document is already a PDF). Sending a rasterized
        // copy would post a picture of a document we already hold.
        if (!Component) continue;
        const optLabel = (docType === primary.value ? primary.label : second.label);
        setBusyLabel(`Preparing ${optLabel}…`);
        const base64 = await captureDocPdf(Component, booking.id);
        attachments.push({
          filename: `${optLabel.toLowerCase().replace(/\s+/g, '-')}-${ref}.pdf`,
          contentBase64: base64,
          documentType: docType,
        });
      }

      setBusyLabel('Sending email…');
      const res = await api.shareByEmail(booking.id, {
        document: document_,
        to: to.trim(),
        cc: ccList.length ? ccList.join(', ') : undefined,
        // Omitted entirely when nothing was captured, rather than sent as [] —
        // the insurance body treats an empty array as "the invoice is missing".
        attachments: attachments.length ? attachments : undefined,
      });

      if (res?.data?.emailed) {
        toast.ok('Sent', `Emailed to ${to.trim()}.`);
        onClose();
      } else {
        // Logged, but the mailer truthfully reports it did not go out (e.g. SMTP
        // not configured in this environment) — never claim a fake success.
        setError('The document was logged, but the email could not be delivered. Check the mail server configuration.');
      }
    } catch (e) {
      setError(e?.response?.data?.error?.message || e?.message || 'Could not prepare or send this document.');
    } finally {
      setBusy(false);
      setBusyLabel('');
    }
  }

  return (
    <div className="lk-overlay" onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className="lk-modal panel glass" style={{ maxWidth: 480 }}>
        <div className="lk-modal-head">
          <h3>Share by Email · #{booking.id}</h3>
          <button className="ib" aria-label="Close" onClick={onClose} disabled={busy}><Icon name="x" /></button>
        </div>

        <div className="det-body">
          <div className="field full">
            <label>Document</label>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              {[...options, ...(canSendBoth
                ? [{ value: 'both', label: `${primary.label} & ${second.label}` }]
                : [])].map((opt) => (
                <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 400 }}>
                  <input
                    type="radio"
                    name="share-document"
                    value={opt.value}
                    checked={document_ === opt.value}
                    onChange={() => setDocument(opt.value)}
                    disabled={busy}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
            {kind === 'insurance' && !hasPolicyDoc ? (
              <div className="muted" style={{ fontSize: '.82em', marginTop: 6 }}>
                The insurer&rsquo;s policy document has not been uploaded for this booking yet, so only the
                invoice can be sent. Upload it under Insurance &rarr; Policy Requests.
              </div>
            ) : null}
          </div>

          <div className="field full">
            <label>Send to <span style={{ color: 'var(--danger, #e5484d)' }}>*</span></label>
            <input
              type="email"
              value={to}
              onChange={(e) => { toTouched.current = true; setTo(e.target.value); }}
              placeholder="customer@example.com"
              disabled={busy}
            />
            {to && !toValid ? <div className="field-error">Enter a valid email address.</div> : null}
          </div>

          <div className="field full">
            <label>CC <span className="muted">(optional, comma-separated)</span></label>
            <input
              value={cc}
              onChange={(e) => setCc(e.target.value)}
              placeholder="ops@example.com, manager@example.com"
              disabled={busy}
            />
            {cc && !ccValid ? <div className="field-error">One or more CC addresses are invalid.</div> : null}
          </div>

          {error ? <div className="field-error">{error}</div> : null}
          {busy ? <p className="muted" style={{ fontSize: '.85em' }}>{busyLabel}</p> : null}

          <div className="form-actions">
            <button className="btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
            <button className="btn primary" onClick={handleSend} disabled={busy || !toValid}>
              <Icon name="mail" /> {busy ? 'Sending…' : 'Send'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
